import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("nv_chat_session");
  if (!id) { id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; localStorage.setItem("nv_chat_session", id); }
  return id;
}

const GREETING = "Hi! I'm Neuravolt AI. Ask me about our services, process, or how we can help build your next product.";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.197 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662a11.87 11.87 0 005.71 1.455h.005c6.554 0 11.89-5.335 11.893-11.893 0-3.178-1.237-6.167-3.486-8.415z" />
  </svg>
);

function FormattedMessage({ content }) {
  if (!content) return null;

  // Normalizing string in case bullet points are crammed together without line breaks
  let normalized = content.replace(/(\* \*\*)/g, "\n* **");
  const lines = normalized.split("\n");
  const elements = [];
  let currentList = [];

  const parseInline = (str) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={idx} className="font-semibold text-[#0B0B0B]">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  lines.forEach((line, lineIdx) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    if (trimmed.startsWith("* ") || trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      const itemText = trimmed.replace(/^[\*\-\•]\s*/, "");
      currentList.push(
        <li key={lineIdx} className="flex items-start gap-2 my-1 text-xs sm:text-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] mt-1.5 shrink-0" />
          <span className="flex-1">{parseInline(itemText)}</span>
        </li>
      );
    } else {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`ul-${lineIdx}`} className="my-2 space-y-1 pl-1">
            {currentList}
          </ul>
        );
        currentList = [];
      }
      elements.push(
        <p key={lineIdx} className="my-1.5 leading-relaxed text-xs sm:text-sm">
          {parseInline(line)}
        </p>
      );
    }
  });

  if (currentList.length > 0) {
    elements.push(
      <ul key={`ul-end`} className="my-2 space-y-1 pl-1">
        {currentList}
      </ul>
    );
  }

  return <div className="space-y-1 text-neutral-800">{elements}</div>;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          const m = line.match(/^data: (.*)$/s);
          if (!m) continue;
          const payload = m[1];
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              setMessages((msgs) => { const c = [...msgs]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.delta }; return c; });
            } else if (data.error) {
              setMessages((msgs) => { const c = [...msgs]; c[c.length - 1] = { role: "assistant", content: data.error }; return c; });
            }
          } catch { /* partial */ }
        }
      }
    } catch {
      setMessages((msgs) => { const c = [...msgs]; c[c.length - 1] = { role: "assistant", content: "Connection error. Please try again." }; return c; });
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <motion.a
        href="https://wa.me/919202243725"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-20 right-5 sm:bottom-24 sm:right-6 z-[60] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#25D366] text-white grid place-items-center shadow-[0_8px_30px_rgba(37,211,102,0.5)] transition-transform group"
        aria-label="Chat on WhatsApp"
        title="Chat with Neuravolt on WhatsApp"
      >
        <WhatsAppIcon />
        <span className="absolute right-full mr-3 hidden sm:group-hover:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0B0B0B] px-3 py-1.5 text-xs font-semibold text-white shadow-xl border border-white/10 pointer-events-none">
          <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
          Chat on WhatsApp
        </span>
      </motion.a>

      {/* Floating AI Chat Toggle Button */}
      <motion.button
        data-testid="chat-toggle"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#FF5A1F] text-white grid place-items-center shadow-[0_8px_40px_rgba(255,90,31,0.5)] group"
        aria-label="Open chat"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute right-full mr-3 hidden sm:group-hover:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0B0B0B] px-3 py-1.5 text-xs font-semibold text-white shadow-xl border border-white/10 pointer-events-none">
            <Sparkles className="w-3.5 h-3.5 text-[#FF5A1F]" />
            Ask NeuraVolt AI
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-panel" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-36 sm:bottom-40 right-3 sm:right-6 z-[60] w-[calc(100vw-1.5rem)] sm:w-[380px] h-[520px] max-h-[75vh] bg-white rounded-3xl flex flex-col overflow-hidden shadow-2xl border border-black/5">
            <div className="flex items-center gap-3 p-5 border-b border-black/5">
              <span className="w-10 h-10 rounded-xl bg-[#FF5A1F] text-white grid place-items-center"><Sparkles className="w-5 h-5" /></span>
              <div>
                <div className="font-heading font-semibold leading-tight text-[#0B0B0B]">Ask Neuravolt AI</div>
                <div className="text-xs text-neutral-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Online · Neuravolt Assistant</div>
              </div>
            </div>
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FAF9F6]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[88%] px-4 py-3 rounded-2xl text-xs sm:text-sm leading-relaxed ${m.role === "user" ? "bg-[#FF5A1F] text-white rounded-br-sm font-medium" : "bg-white border border-black/5 text-neutral-800 rounded-bl-sm shadow-sm"}`}>
                    {m.content ? <FormattedMessage content={m.content} /> : <span className="inline-flex gap-1 py-1"><span className="w-1.5 h-1.5 bg-[#FF5A1F] rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-[#FF5A1F] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} /><span className="w-1.5 h-1.5 bg-[#FF5A1F] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} /></span>}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-4 border-t border-black/5 flex gap-2 bg-white">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message..."
                className="flex-1 bg-[#FAF9F6] border border-black/10 rounded-full px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors placeholder:text-neutral-400 text-[#0B0B0B]" />
              <button data-testid="chat-send" type="submit" disabled={loading || !input.trim()} className="w-11 h-11 rounded-full bg-[#FF5A1F] text-white grid place-items-center hover:bg-[#ff6d38] transition-colors disabled:opacity-50 shrink-0"><Send className="w-4 h-4" /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;

